import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { BatchTagPrintingData } from '@/adapters/offline-mode';
import {
  BORDER_RADIUS_MEDIUM,
  BORDER_RADIUS_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { formatDateWithYear } from '@/utils/date';
import {
  PrintTagBatchListFieldsFragment,
  TagPrint_BatchListQuery,
  TagPrint_BatchListQueryVariables,
  TagPrint_BatchRequestDeliveryMutation,
  TagPrint_BatchRequestDeliveryMutationVariables,
} from '@/types';
import {
  REQUEST_PRINT_TAG_BATCH_DELIVERY_MUTATION,
  TAG_PRINT_BATCH_LIST_QUERY,
} from '@/components/tag-print-batch-queries';
import { StackNavigationProp } from '@react-navigation/stack';
import { TagPrintBatchConfirmationActionSheet } from '@/components/tag-print-batch-confirmation-action-sheet';
import { TagPrintBatchListActionSheet } from '@/components/tag-print-batch-list-action-sheet';
import { Text, TouchableOpacity, View } from 'react-native';
import { Type } from '@/components/toast';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { useToasts } from '@/contexts/toast-context';

const ACTIONS_HIT_SLOP = {
  bottom: 8,
  left: 16,
  right: 16,
  top: 8,
};

type Navigation = StackNavigationProp<
  AppContainerStackParams,
  APP_CONTAINER_ROUTES.TagPrintBatch
>;

interface Props {
  batch: PrintTagBatchListFieldsFragment;
  offlineTagBatchPrintData: BatchTagPrintingData | null;
  updateTagPrintBatchData: (
    tagBatchPrintData: BatchTagPrintingData,
  ) => Promise<void>;
}

export const TagPrintBatchListItem: React.FC<Props> = ({
  batch,
  offlineTagBatchPrintData,
  updateTagPrintBatchData,
}) => {
  const [styles, theme] = useStyles();
  const { navigate } = useNavigation<Navigation>();
  const { showToast, showErrorToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useAuthenticatedUserContext();

  const [showListActionSheet, setShowListActionSheet] = useState(false);
  const [showConfirmationActionSheet, setShowConfirmationActionSheet] =
    useState(false);
  const [selectedOfflineTemplateUUID, setSelectedOfflineTemplateUUID] =
    useState<string | null>(null);

  const printTagBatchUUID = batch.uuid;
  const tagCount =
    batch.print_tag_batch_variant_packs_aggregate?.aggregate?.sum?.quantity ??
    0;
  const editedDate =
    batch.print_tag_batch_variant_packs_aggregate?.aggregate?.max?.updated_at;

  const selectedOfflineBatch =
    offlineTagBatchPrintData?.batches?.[printTagBatchUUID];

  const selectedOfflineBatchTemplateUUID =
    selectedOfflineBatch?.print_tag_template.uuid;

  const selectedOfflineTemplate = selectedOfflineBatchTemplateUUID
    ? offlineTagBatchPrintData?.templates?.[selectedOfflineBatchTemplateUUID]
    : null;

  const [requestPrintTagDelivery] = useMutation<
    TagPrint_BatchRequestDeliveryMutation,
    TagPrint_BatchRequestDeliveryMutationVariables
  >(REQUEST_PRINT_TAG_BATCH_DELIVERY_MUTATION, {
    update: (client, { data }) => {
      const requestedBatch = data?.requestPrintTagBatchDelivery.print_tag_batch;
      try {
        const tagPrintBatchListQuery = client.readQuery<
          TagPrint_BatchListQuery,
          TagPrint_BatchListQueryVariables
        >({
          query: TAG_PRINT_BATCH_LIST_QUERY,
          variables: {
            restaurantUUID,
            supplierUUID,
          },
        });

        if (!tagPrintBatchListQuery?.print_tag_batches) {
          return;
        }

        const updatedTagPrintBatchList =
          tagPrintBatchListQuery.print_tag_batches.map((item) => {
            if (item.uuid === requestedBatch?.uuid) {
              return requestedBatch;
            }
            return item;
          });

        if (tagPrintBatchListQuery) {
          client.writeQuery<TagPrint_BatchListQuery>({
            data: {
              print_tag_batches: updatedTagPrintBatchList,
            },
            query: TAG_PRINT_BATCH_LIST_QUERY,
            variables: {
              restaurantUUID,
              supplierUUID,
            },
          });
        }
      } catch (e) {
        captureException(e);
      }
    },
    variables: {
      printTagBatchUUID,
      restaurantUUID,
      supplierUUID,
    },
  });

  const onRequestDeliveryOfTags = async () => {
    setShowListActionSheet(false);
    try {
      await requestPrintTagDelivery();
      showToast({
        content: i18n.t('toast.batch_requested', {
          batchName: batch.name,
        }),
        type: Type.POSITIVE,
      });
    } catch {
      showErrorToast();
    }
  };

  const displayDate = useMemo(() => {
    const wasRequested = Boolean(batch.requested_at);
    const requestedMoment = wasRequested ? moment(batch.requested_at) : null;
    const editedMoment = moment(editedDate ?? batch.created_at);

    const mostRecentMoment = requestedMoment
      ? moment.max(requestedMoment, editedMoment)
      : editedMoment;

    const isMostRecentlyRequested = mostRecentMoment === requestedMoment;

    return i18n.t(isMostRecentlyRequested ? 'requested_date' : 'edited_date', {
      date: formatDateWithYear(mostRecentMoment),
    });
  }, [batch.created_at, batch.requested_at, editedDate]);

  return (
    <>
      <TouchableOpacity
        onPress={() => {
          navigate(APP_CONTAINER_ROUTES.TagPrintBatch, {
            printTagBatchUUID,
          });
        }}
        style={styles.listItem}
      >
        <View style={styles.row}>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.pSemiBold, styles.nameText]}
          >
            {batch.name}
          </Text>
          <Button
            hitSlop={ACTIONS_HIT_SLOP}
            Icon={MenuHorizontalIcon}
            iconProps={{ fill: theme.sk.color.icon.secondary }}
            onPress={() => setShowListActionSheet(true)}
            size="small"
            style={styles.actionButton}
            testID="tag-print-batch-list-actions-icon"
            trackingData={{
              name: 'batch_tag_print.list_actions',
              properties: {
                printTagBatchUUID,
              },
            }}
            type="tertiary"
          />
        </View>
        <View style={styles.row}>
          <View style={styles.tagQuantity}>
            <Text style={[TYPOGRAPHY.pSemiBold, styles.tagQuantityText]}>
              {tagCount === 1
                ? i18n.t('tag_count.singular')
                : i18n.t('tag_count.plural', {
                    count: tagCount,
                  })}
            </Text>
          </View>
          <Text style={[TYPOGRAPHY.p, styles.dateText]}>{displayDate}</Text>
        </View>
      </TouchableOpacity>
      {showListActionSheet ? (
        <TagPrintBatchListActionSheet
          canUseSelectedPrintTemplateOffline={Boolean(
            selectedOfflineTemplate?.offline_printer_text_template,
          )}
          onClose={() => setShowListActionSheet(false)}
          onDelete={() => {
            showToast({
              content: i18n.t('toast.batch_deleted'),
              type: Type.POSITIVE,
            });
            setShowListActionSheet(false);
            if (offlineTagBatchPrintData) {
              const deletedOfflineBatch =
                offlineTagBatchPrintData.batches?.[printTagBatchUUID];
              if (deletedOfflineBatch) {
                const updatedOfflineBatches = {
                  ...offlineTagBatchPrintData.batches,
                };
                delete updatedOfflineBatches[printTagBatchUUID];

                updateTagPrintBatchData({
                  ...offlineTagBatchPrintData,
                  batches: updatedOfflineBatches,
                });
              }
            }
          }}
          onRequestDeliveryOfTags={onRequestDeliveryOfTags}
          onSelectPrint={() => {
            setShowConfirmationActionSheet(true);
            setShowListActionSheet(false);
          }}
          printTagBatchUUID={printTagBatchUUID}
          setSelectedOfflineTemplateUUID={setSelectedOfflineTemplateUUID}
        />
      ) : null}
      {showConfirmationActionSheet ? (
        <TagPrintBatchConfirmationActionSheet
          onClose={() => setShowConfirmationActionSheet(false)}
          onComplete={() => {
            setShowConfirmationActionSheet(false);
            showToast({
              autoCloseTimeout: 3000,
              content:
                tagCount > 1
                  ? i18n.t('batch_print_tags.sent_plural', { count: tagCount })
                  : i18n.t('batch_print_tags.sent_singular'),
              type: Type.POSITIVE,
            });
          }}
          printTagBatchUUID={printTagBatchUUID}
          selectedOfflineTemplateUUID={selectedOfflineTemplateUUID}
          tagCount={tagCount}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    paddingHorizontal: 0,
  },
  dateText: {
    color: theme.sk.color.text.secondary,
  },
  listItem: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: BORDER_RADIUS_SMALL,
    gap: 16,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  nameText: {
    flex: 1,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tagQuantity: {
    borderColor: theme.sk.color.border.secondary,
    borderRadius: BORDER_RADIUS_MEDIUM,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 2,
  },
  tagQuantityText: {
    color: theme.sk.color.text.secondary,
  },
}));
